import React, { useEffect, useState } from "react";
import { X, Trash } from "lucide-react";
import { useTranslation } from "react-i18next";
import { QuantityControl } from "../UI/QuantityControl";
import { ServiceSnapshot } from "../../types";
import { Service } from "../../types/service";

interface AddServiceModalProps {
  show: boolean;
  onClose: () => void;
  availableServices: Service[];
  existingServices: ServiceSnapshot[];
  onSave: (services: ServiceSnapshot[]) => void;
}

export const AddServiceModal: React.FC<AddServiceModalProps> = ({
  show,
  onClose,
  availableServices,
  existingServices,
  onSave,
}) => {
  const { t } = useTranslation("shipments");

  const [services, setServices] = useState<ServiceSnapshot[]>([]);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [customServiceName, setCustomServiceName] = useState("");
  const [customServiceUnitType, setCustomServiceUnitType] = useState("");
  const [serviceQuantity, setServiceQuantity] = useState("1");
  const [serviceDescription, setServiceDescription] = useState("");

  // Load existing services
  useEffect(() => {
    if (existingServices) setServices(existingServices);
  }, [existingServices]);

  const handleAddService = () => {
    let selected: ServiceSnapshot | null = null;

    if (selectedServiceId === "other") {
      if (!customServiceName || !customServiceUnitType) return;
      selected = {
        name: customServiceName,
        type: customServiceUnitType.toUpperCase() as ServiceSnapshot["type"],
        price: 0,
        qty: Number(serviceQuantity),
        total: 0,
        description: serviceDescription,
        addedBy: "driver",
      };
    } else {
      const found = availableServices.find((s) => s._id === selectedServiceId);
      if (!found) return;
      selected = {
        name: found.name,
        type: found.type,
        price: found.price || 0,
        qty: Number(serviceQuantity),
        total: (found.price || 0) * Number(serviceQuantity),
        description: found.description ?? "",
        addedBy: "driver",
        /**
         * The card's VAT rate, and which card it was.
         *
         * Both used to be left behind here, and neither absence announced
         * itself. `tax` defaults to "NO_TAX" on the row, which is a legitimate
         * value — so a service that should carry 24% was billed with no VAT at
         * all and looked exactly like a service that correctly has none.
         *
         * `catalogServiceId` is how invoicing finds the card again for the
         * accounting mapping and the invoice-detail selection. Without it the
         * line resolves by CODE, and a code can name two cards — the general
         * rate and a client's own — so the row could be booked against a
         * revenue account belonging to a card nobody picked.
         */
        tax: found.tax ?? "NO_TAX",
        catalogServiceId: found._id,
      };
    }

    setServices((prev) => [...prev, selected!]);
    resetFields();
  };

  const resetFields = () => {
    setSelectedServiceId("");
    setCustomServiceName("");
    setCustomServiceUnitType("");
    setServiceQuantity("1");
    setServiceDescription("");
  };

  const handleRemoveService = (index: number) => {
    setServices((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Send the rows as they are, not a copy of seven of their fields.
   *
   * This list contains rows LOADED from the shipment as well as rows added
   * just now, and the endpoint replaces every driver row with what it is sent.
   * Rebuilding each one from a whitelist therefore did not merely drop `tax`
   * and `catalogServiceId` — it dropped `_id`, so a row that already existed
   * came back as a different row every time this dialog was saved. An invoice
   * line points at a service row BY `_id`, so the draft billing it was left
   * pointing at nothing and could no longer be locked.
   *
   * Server-owned fields (`invoiceId`, `invoicedAt`) are stripped by the
   * validator and put back from storage by the endpoint, so passing them
   * through is both harmless and the only way the server can match a row to
   * the one it already holds.
   */
  const handleSave = () => {
    const payload: ServiceSnapshot[] = services.map((s) => ({
      ...s,
      addedBy: s.addedBy || "driver",
    }));

    onSave(payload);
    onClose();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-dark-900">
            {t("addService.title")}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Add New Service Section */}
        <div className="space-y-4 border-b border-gray-300 pb-4">
          <div>
            <label className="block text-sm font-medium text-dark-700 mb-1">
              {t("addService.serviceCodeLabel")}
            </label>
            <select
              value={selectedServiceId}
              onChange={(e) => setSelectedServiceId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">{t("addService.selectService")}</option>
              {availableServices.map((service) => (
                <option key={service._id} value={service._id}>
                  {service.name}
                </option>
              ))}
            </select>
          </div>

          {selectedServiceId === "other" ? (
            <>
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">
                  {t("addService.serviceNameLabel")}
                </label>
                <input
                  type="text"
                  value={customServiceName}
                  onChange={(e) => setCustomServiceName(e.target.value)}
                  placeholder={t("addService.serviceNamePlaceholder")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">
                  {t("addService.unitTypeLabel")}
                </label>
                <select
                  value={customServiceUnitType}
                  onChange={(e) => setCustomServiceUnitType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">{t("addService.selectUnitType")}</option>
                  <option value="KM">KM</option>
                  <option value="HOUR">HOUR</option>
                  <option value="PIECE">PIECE</option>
                  <option value="LIFT">LIFT</option>
                  <option value="FLAT">FLAT</option>
                  <option value="QTY">QTY</option>
                </select>
              </div>
            </>
          ) : null}

          <div>
            <QuantityControl
              label={t("addService.quantityLabel")}
              value={parseInt(serviceQuantity) || 0}
              onChange={(value) => setServiceQuantity(value.toString())}
              min={1}
              max={9999}
              placeholder="1"
            />
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleAddService}
              disabled={
                !selectedServiceId ||
                (selectedServiceId === "other" &&
                  (!customServiceName || !customServiceUnitType))
              }
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("addService.add")}
            </button>
          </div>
        </div>

        {/* Existing Services */}
        <div className="mt-4">
          <h3 className="text-lg font-medium text-dark-800 mb-2">
            {t("addService.addedServicesTitle")}
          </h3>
          {services.length === 0 ? (
            <p className="text-gray-500 text-sm">
              {t("addService.noServicesAdded")}
            </p>
          ) : (
            <ul className="divide-y divide-gray-300">
              {services.map((s, idx) => (
                <li
                  key={idx}
                  className="flex justify-between items-center py-2"
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      {s.name} (
                      <span className="text-sm font-normal">{s.type}</span>)
                    </p>
                    <p className="text-sm text-gray-500">
                      {t("addService.quantity", { qty: s.qty })}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemoveService(idx)}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash className="w-5 h-5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end mt-6 space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {t("addService.cancel")}
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            {t("addService.save")}
          </button>
        </div>
      </div>
    </div>
  );
};
